import { z } from "zod";

/**
 * 第八域本地优先执行的协议契约（`local-coaching-v1`）。
 *
 * 这里只放**可以离开浏览器的东西**。数据面规则见规格 §5.7：页面正文、表单值、选中元素、
 * 商品明细与素材一律留在本地；服务端只允许持久化摘要、执行头、一次性授权与调用回执。
 * 所以本文件的 Schema 里不会出现 `visibleText`、`forms`、`values` 这类字段——需要正文的
 * 东西不进请求体，而不是"进了再过滤"。
 */

export const LOCAL_COACHING_PROTOCOL = "local-coaching-v1" as const;

/** §5.3：标题上限 120 字，成果摘要上限 300 字，序列化单条载荷上限 4 KiB。 */
export const LOCAL_SUMMARY_LIMITS = {
  titleChars: 120,
  outcomeChars: 300,
  payloadBytes: 4096,
} as const;

/** §5.3：自动重试间隔 5s / 30s / 2min / 10min，之后每 10 分钟；没有队列不持续轮询。 */
export const LOCAL_SYNC_RETRY_MS = [5_000, 30_000, 120_000, 600_000] as const;

export const LocalTaskStatusSchema = z.enum([
  "planning", "awaiting_confirmation", "running", "verifying",
  "waiting_user", "waiting_network", "interrupted",
  "completed", "stopped", "failed",
]);
export type LocalTaskStatus = z.infer<typeof LocalTaskStatusSchema>;

/**
 * 只传域名：**不允许**含查询参数的页面 URL（查询串常常带令牌与个人参数）。
 * 允许空字符串，因为有的任务确实没有目标页。
 */
export const LocalPageDomainSchema = z.union([
  z.literal(""),
  z.string().min(1).max(240).regex(/^[a-z0-9.-]+$/i, "只允许域名，不允许路径或查询参数"),
]);

/** §5.2 local_task_summaries：允许持久化的全部字段。 */
export const LocalTaskSummarySchema = z.object({
  taskId: z.string().min(1).max(160),
  /** 服务端权威来源是会话；这里的 deviceId 只用于绑定与本机区分。 */
  deviceId: z.string().uuid(),
  environmentId: z.string().min(1).max(120),
  title: z.string().min(1).max(LOCAL_SUMMARY_LIMITS.titleChars),
  status: LocalTaskStatusSchema,
  /** 短成果：由最小化投影生成，不是直接截取回答。 */
  outcome: z.string().max(LOCAL_SUMMARY_LIMITS.outcomeChars),
  domain: LocalPageDomainSchema,
  /** 单调递增；旧 revision 不允许覆盖新值。 */
  revision: z.number().int().nonnegative(),
  updatedAt: z.string(),
}).strict();
export type LocalTaskSummary = z.infer<typeof LocalTaskSummarySchema>;

/** PUT /summaries/:taskId 的响应：幂等或冲突都要能被客户端区分。 */
export const LocalSummaryWriteResultSchema = z.discriminatedUnion("result", [
  z.object({ result: z.literal("created"), revision: z.number().int().nonnegative() }).strict(),
  /** 同 revision 同内容：幂等，不报错也不改动。 */
  z.object({ result: z.literal("unchanged"), revision: z.number().int().nonnegative() }).strict(),
  /** 同 revision 不同内容，或旧 revision 想覆盖新值：客户端必须自己解决，服务端不猜。 */
  z.object({
    result: z.literal("conflict"),
    revision: z.number().int().nonnegative(),
    serverRevision: z.number().int().nonnegative(),
  }).strict(),
]);
export type LocalSummaryWriteResult = z.infer<typeof LocalSummaryWriteResultSchema>;

export const LocalSummaryListSchema = z.object({
  summaries: z.array(LocalTaskSummarySchema).max(100),
  /** 还有更多时给出下一页游标；没有则省略。 */
  cursor: z.string().max(200).optional(),
}).strict();
export type LocalSummaryList = z.infer<typeof LocalSummaryListSchema>;

export const LocalCapabilitiesSchema = z.object({
  enabled: z.boolean(),
  protocol: z.literal(LOCAL_COACHING_PROTOCOL),
  limits: z.object({
    titleChars: z.number().int().positive(),
    outcomeChars: z.number().int().positive(),
    payloadBytes: z.number().int().positive(),
  }).strict(),
  policyVersion: z.string().min(1).max(80),
}).strict();
export type LocalCapabilities = z.infer<typeof LocalCapabilitiesSchema>;

/**
 * 同步单条载荷的字节上限按 UTF-8 计算——按字符算会让中文载荷实际超出预算。
 * 超限时调用方必须裁掉**可选**部分，不能截断标题或成果。
 */
export function localSummaryPayloadBytes(summary: LocalTaskSummary): number {
  return new TextEncoder().encode(JSON.stringify(summary)).length;
}

/* -------------------------------------------------------------------------- */
/* §5.5 签名计划与动作许可                                                     */
/* -------------------------------------------------------------------------- */

/**
 * 固定算法，且**只**由服务端决定：契约里没有"算法"字段，信封里的 `keyId` 选的是密钥而不是算法。
 * 客户端在任何一层塞 `algorithm` / `alg` 都会被服务端按独立错误码拒绝（见 API 层）。
 */
export const LOCAL_PLAN_SIGNING_ALGORITHM = "hmac-sha256" as const;

/** §5.5：最多 3 段、每段 3 个动作、许可最长 10 分钟；到期不自动续签。 */
export const LOCAL_PLAN_LIMITS = {
  maxSegments: 3,
  maxActionsPerSegment: 3,
  authorizationTtlSeconds: 600,
} as const;

/** §5.5 固定绑定：actor / environmentId / deviceId / taskId / planVersion / actionIds / 参数哈希 / 依据版本 / 有效期。 */
export const LocalPlanCredentialPayloadSchema = z.object({
  /** 只从会话取得；签名时由服务端写入，请求体里带 actor 会被严格 Schema 拒绝。 */
  actor: z.string().min(1).max(120),
  environmentId: z.string().min(1).max(120),
  deviceId: z.string().uuid(),
  taskId: z.string().min(1).max(160),
  planVersion: z.string().min(1).max(80),
  actionIds: z.array(z.string().uuid()).min(1).max(LOCAL_PLAN_LIMITS.maxActionsPerSegment),
  /** 已确认参数集的摘要；本地改参数会算出不同哈希，因此不能沿用旧许可。 */
  parameterHash: z.string().regex(/^[a-f\d]{64}$/),
  /** 依据版本（现场与依据的版本号）。 */
  evidenceVersion: z.string().min(1).max(80),
  issuedAt: z.string().min(1).max(40),
  expiresAt: z.string().min(1).max(40),
}).strict();
export type LocalPlanCredentialPayload = z.infer<typeof LocalPlanCredentialPayloadSchema>;

/**
 * 凭据信封。`digest` **不是**算法选择器，也不参与授权判断：
 * 它只是把"载荷被改"与"签名被改"两种失败区分开（§5.5 的篡改语义要求两者各有独立错误码），
 * 授权与否永远由 HMAC-SHA256 签名决定。
 */
export const LocalPlanCredentialSchema = z.object({
  keyId: z.string().min(1).max(80),
  /** base64url(UTF-8 规范序 JSON)，完整计划正文不在服务端落库。 */
  payload: z.string().min(1).max(8192),
  digest: z.string().min(1).max(200),
  signature: z.string().min(1).max(200),
}).strict();
export type LocalPlanCredential = z.infer<typeof LocalPlanCredentialSchema>;

/** §5.4 `POST /actions/authorize` 的请求体；actor 不在其中，只从会话取。 */
export const LocalActionAuthorizeRequestSchema = z.object({
  requestId: z.string().min(1).max(160),
  credential: LocalPlanCredentialSchema,
  actionId: z.string().uuid(),
  parameterHash: z.string().regex(/^[a-f\d]{64}$/),
  environmentId: z.string().min(1).max(120),
  deviceId: z.string().uuid(),
}).strict();
export type LocalActionAuthorizeRequest = z.infer<typeof LocalActionAuthorizeRequestSchema>;

/** 一次性领取成功回执；重复领取是错误码而不是同名结果（客户端必须能区分"没领到"与"已领过"）。 */
export const LocalActionAuthorizeResultSchema = z.object({
  result: z.literal("claimed"),
  actionId: z.string().uuid(),
  taskId: z.string().min(1).max(160),
  planId: z.string().min(1).max(160),
  claimedAt: z.string(),
  expiresAt: z.string(),
}).strict();
export type LocalActionAuthorizeResult = z.infer<typeof LocalActionAuthorizeResultSchema>;

/* -------------------------------------------------------------------------- */
/* §5.4 GET /calls/:requestId 与 §5.6 不缓存正文                                */
/* -------------------------------------------------------------------------- */

export const LocalCallStageSchema = z.enum(["plan", "plan_next", "confirm", "analyze", "action"]);
export const LocalCallStatusSchema = z.enum(["started", "succeeded", "failed", "rejected"]);
export type LocalCallStage = z.infer<typeof LocalCallStageSchema>;
export type LocalCallStatus = z.infer<typeof LocalCallStatusSchema>;

/**
 * 调用回执只允许状态、用量与结算信息。这里刻意**没有**任何能装模型输入／输出的字段，
 * 所以"用 requestId 补取原回答"在类型层就不存在，而不是靠运行时记得别返回（§5.6）。
 */
export const LocalCallReceiptSchema = z.object({
  requestId: z.string().min(1).max(160),
  stage: LocalCallStageSchema,
  status: LocalCallStatusSchema,
  model: z.string().max(120).optional(),
  promptVersion: z.string().max(80).optional(),
  promptHash: z.string().regex(/^[a-f\d]{64}$/).optional(),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  durationMs: z.number().int().nonnegative(),
  billingId: z.string().max(160).optional(),
  errorCode: z.string().max(80).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).strict();
export type LocalCallReceipt = z.infer<typeof LocalCallReceiptSchema>;
